import { Link } from "react-router";
import { NotConnectedState } from "@/components/states";
import { PageHeader } from "@/components/bespoke";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

/** A short statement of what a future policy section will hold. */
export function ProjectSettingsPlaceholder({
  title,
  summary,
  headingLevel = "h2",
  link,
}: {
  title: string;
  summary: string;
  headingLevel?: "h1" | "h2";
  link?: { to: string; label: string };
}) {
  const Heading = headingLevel;
  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <div>
        <Heading className="text-lg font-semibold tracking-tight">{title}</Heading>
        <p className="mt-1 text-sm text-muted-foreground">{summary}</p>
        {link ? (
          <p className="mt-2">
            <Link
              to={link.to}
              className="text-sm font-medium text-accent-text underline-offset-4 hover:underline"
            >
              {link.label}
            </Link>
          </p>
        ) : null}
      </div>
      <Card>
        <CardContent>
          <NotConnectedState
            title={`${title} is not connected yet`}
            description="The controls for this section need their backend capability first. Nothing here spends credits or changes the project."
          />
        </CardContent>
      </Card>
    </div>
  );
}

/** The danger zone heading block with its labelled badge, shared by the page and dialog. */
export function DangerZoneHeading({ headingLevel = "h2" }: { headingLevel?: "h1" | "h2" }) {
  const Heading = headingLevel;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Heading className="text-lg font-semibold tracking-tight text-danger">Danger zone</Heading>
      <Badge variant="danger">Owner only</Badge>
    </div>
  );
}

/** The archive action stays with General today; the danger zone section links back to it. */
export function DangerZoneSection({ projectName }: { projectName: string }) {
  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <div>
        <DangerZoneHeading />
        <p className="mt-1 text-sm text-muted-foreground">
          Irreversible actions for {projectName}. Archive lives under General today; transfer and
          deletion of the project live under Members and Data controls.
        </p>
      </div>
      <Card className="border-danger/40">
        <CardContent className="flex flex-col gap-2">
          <p className="font-medium">Transfer ownership</p>
          <p className="text-sm text-muted-foreground">
            Transferring the project to another owner happens from the Members and roles section.
            The new owner takes over billing and credits.
          </p>
          <p>
            <Link
              to="/settings/members"
              className="text-sm font-medium text-accent-text underline-offset-4 hover:underline"
            >
              Go to Members and roles
            </Link>
          </p>
        </CardContent>
      </Card>
      <Card className="border-danger/40">
        <CardContent className="flex flex-col gap-2">
          <p className="font-medium">Archive or delete {projectName}</p>
          <p className="text-sm text-muted-foreground">
            Archiving blocks new runs and lives under General. Deletion needs a second owner and
            lives under Data and retention.
          </p>
          <p className="flex flex-wrap gap-4">
            <Link
              to="/settings/general"
              className="text-sm font-medium text-accent-text underline-offset-4 hover:underline"
            >
              Go to General
            </Link>
            <Link
              to="/settings/data-retention"
              className="text-sm font-medium text-accent-text underline-offset-4 hover:underline"
            >
              Go to Data and retention
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

/** The project settings page header: name, role, and an owner-only marker. */
export function ProjectSettingsHeader({
  projectName,
  roleLabel,
}: {
  projectName: string;
  roleLabel: string;
}) {
  return (
    <PageHeader title="Project settings" description={`${projectName} · you are ${roleLabel}`} />
  );
}
